import React, { useState, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { exportToDocx, exportToFile, exportToPdf } from './ExportUtils';
import MoreFormatsMenu from '../transcript/MoreFormatsMenu';
import { PROTOCOL_FORMATS, buildProtocolExport } from '../../lib/protocolExport';
import { buildDownloadName } from '../../utils/downloadName';
import { rehypeSearchHits } from '../searchHits';
import { copyRich, htmlToPlain } from '../../lib/richClipboard';

// canEdit=false — режим просмотра без правки (История, чужая запись). Переключателя
// в этом случае нет вовсе: раньше он там был, но `onResultChange` приходил пустой
// заглушкой, и человек печатал в поле, которое молча ничего не сохраняло.
// saveStatus — подпись о сохранении правки рядом с кнопками ('Сохраняется…',
// 'Сохранено', текст ошибки); saveFailed красит её как ошибку. Нужна там, где
// правка уходит на сервер не мгновенно (История), — иначе её не отличить от
// прежнего поля, которое ничего не сохраняло.
export default function ProtocolTab({
  resultText, isProcessing, taskStatus, onResultChange,
  onMoreDetail, onLessDetail, canMore, canLess, levelLabel, sourceFileName,
  canEdit = true, highlight, saveStatus = null, saveFailed = false,
}) {
  const [viewMode, setViewMode] = useState('preview');
  const [copyState, setCopyState] = useState(null); // null | 'done' | 'failed'
  const markdownRef = useRef(null);
  const hasResult = !!resultText;

  // Отказ буфера (нет разрешения, окно не в фокусе, старый браузер) — подпись
  // «Не скопировалось», а не необработанное исключение из обработчика.
  const handleCopy = async () => {
    if (!resultText) return;
    let next = 'done';
    try {
      // Оформленный текст и простой без разметки, а не сырой Markdown: вставка в Word,
      // почту или Лензу читается (lib/richClipboard.js). 🔴 До copyRich ничего не await-ить:
      // Safari пускает в буфер только в том же такте, что и клик. Картинки в копию не берём —
      // Word при вставке сам пошёл бы по их адресу.
      const html = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]} disallowedElements={['img']}>{resultText}</ReactMarkdown>);
      await copyRich(html, htmlToPlain(html));
    } catch {
      next = 'failed';
    }
    setCopyState(next);
    setTimeout(() => setCopyState(null), 1500);
  };
  const copied = copyState === 'done';
  const copyLabel = copied ? 'Скопировано' : copyState === 'failed' ? 'Не скопировалось' : 'Копировать';

  const disabledBtn = 'opacity-40 cursor-not-allowed';

  const handleExport = (key) => {
    if (!hasResult) return;
    const file = buildProtocolExport(key, resultText);
    exportToFile(file.content, buildDownloadName(sourceFileName, 'prtcl', file.ext), file.mime);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar — всегда видим */}
      {/* Телефон: кнопки одной строкой, лишние уезжают вбок — тремя строками
          панель съедала треть экрана над текстом (тест владельца 27.09). */}
      <div className="kbd-hide flex items-center justify-between gap-2 py-2 border-b border-paper-line">
        <div className="hidden md:flex items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">Протокол</span>
        </div>
        <div className="flex flex-nowrap items-center gap-2 min-w-0 overflow-x-auto md:overflow-visible [&>*]:shrink-0">
          {saveStatus && (
            <span role="status" data-testid="protocol-save-status"
                  className={`text-[12px] ${saveFailed ? 'text-destructive' : 'text-paper-ink-3'}`}>
              {saveStatus}
            </span>
          )}
          {canEdit && (
            <div className="flex bg-paper-bg rounded-[8px] p-1 gap-1">
              <button
                onClick={() => setViewMode('preview')}
                className={`rounded-[6px] px-[10px] py-[3px] text-[12px] font-medium transition-colors ${
                  viewMode === 'preview' ? 'bg-paper-card text-paper-ink' : 'text-paper-ink-3'
                }`}
              >
                Просмотр
              </button>
              <button
                onClick={() => setViewMode('text')}
                className={`rounded-[6px] px-[10px] py-[3px] text-[12px] font-medium transition-colors ${
                  viewMode === 'text' ? 'bg-paper-card text-paper-ink' : 'text-paper-ink-3'
                }`}
              >
                Редактирование
              </button>
            </div>
          )}
          <button
            onClick={handleCopy}
            disabled={!hasResult}
            className={`flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasResult ? disabledBtn : ''}`}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-paper-success" /> : <Copy className="w-3.5 h-3.5" />}
            {/* 🔴 Подпись в <span>, а не голым текстом рядом с иконкой. Перевод
                страницы браузером и подобные расширения заворачивают текстовые
                узлы в свои <font>; при смене иконки React вставляет новый svg
                «перед текстом», которого в кнопке уже нет, — NotFoundError в
                коммите, и граница ошибок сносит всё приложение вместе с
                расшифровкой (обращение 5b1d9f3b, 15.09.2026). */}
            <span>{copyLabel}</span>
          </button>
          <button
            onClick={() => hasResult && exportToDocx(resultText, buildDownloadName(sourceFileName, 'prtcl', 'docx'))}
            disabled={!hasResult}
            className={`flex items-center gap-1 rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasResult ? disabledBtn : ''}`}
          >
            <Download className="w-3.5 h-3.5" /> DOCX
          </button>
          {/* Markdown и простой текст — за стрелкой у PDF, как у расшифровки. */}
          <MoreFormatsMenu items={PROTOCOL_FORMATS} onPick={handleExport}
            disabled={!hasResult}
            chevronClassName={`rounded-[8px] rounded-l-none bg-paper-bg py-[5px] text-paper-ink hover:bg-paper-panel ${!hasResult ? disabledBtn : ''}`}>
            <button
              onClick={() => hasResult && exportToPdf(resultText, buildDownloadName(sourceFileName, 'prtcl', 'pdf'))}
              disabled={!hasResult}
              className={`flex items-center gap-1 rounded-[8px] rounded-r-none bg-paper-bg px-[10px] py-[5px] text-[12px] text-paper-ink hover:bg-paper-panel ${!hasResult ? disabledBtn : ''}`}
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
          </MoreFormatsMenu>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col p-4">
        {isProcessing && !resultText ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 text-paper-hint">
            <Loader2 className="w-8 h-8 animate-spin text-paper-accent" />
            <span className="text-sm">
              {taskStatus === 'pending' ? 'Ожидание в очереди...' : 'Генерация протокола...'}
            </span>
          </div>
        ) : !resultText ? (
          <div className="flex items-center justify-center flex-1 text-sm text-paper-hint">
            Здесь появится сформированный протокол
          </div>
        ) : (viewMode === 'preview' || !canEdit) ? (
          <div className="flex-1 overflow-y-auto">
            <div
              ref={markdownRef}
              className="prose prose-sm max-w-none text-paper-ink prose-headings:font-semibold prose-headings:text-paper-ink prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-ul:my-1 prose-li:my-0.5
                prose-strong:text-paper-ink
                prose-p:leading-relaxed"
            >
              {/* Подсветка найденного — rehype-плагином по текстовым узлам дерева,
                  не строкой HTML: разметка протокола остаётся разметкой. */}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={highlight ? [[rehypeSearchHits, highlight]] : undefined}
              >
                {resultText}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <textarea
            value={resultText}
            onChange={(e) => onResultChange(e.target.value)}
            className="flex-1 min-h-0 w-full text-sm font-mono text-paper-ink-2 border-0 resize-none focus:ring-0 focus:outline-none overflow-y-auto"
          />
        )}
      </div>
    </div>
  );
}
